/**
 * Consumer trips CRUD.
 * localStorage is the working cache; sync uses Device Sync (/devicesync)
 * so trips match the iOS app (not jsontransfer).
 */

import { getUserSubFromToken } from './apiClient';
import {
  catalogRecordId,
  cityVisitRecordId,
  fetchAllDeviceSyncChanges,
  getDeviceSync,
  putDeviceSync,
  tripRecordId,
  type DeviceSyncRecord,
} from './deviceSyncService';
import {
  applyDeviceSyncRecords,
  buildGranularCityVisitBody,
  decodeCityOption,
  decodeCityVisit,
  encodeCityVisit,
  hotelStaysToTrip,
  itineraryDaysFromCityVisit,
} from './deviceSync';
import { tripsFingerprintMatch, orphanTripIdsToSkipPush } from '../utils/tripDedup';
import { parseContentTimestamp, stampTripContent } from '../utils/contentTimestamps';
import { taskItemsFromDefaultTemplate } from '../utils/tripTaskTemplate';
import {
  claimNilOwnerTrip,
  claimNilOwnerTrips,
  getActiveSharedTripIds,
  isAccessibleTrip,
  isOwnedTrip,
  visibleTrips,
} from '../utils/tripAccessPolicy';
import { DEVICE_SYNC_EXPORT_SCHEMA_VERSION } from './deviceSync/types';
import {
  getCityWireShadow,
  getTripWireShadow,
  mergeCityVisitWireWithWebEdits,
  mergeWireBodies,
  setCityWireShadow,
  setTripWireShadow,
} from './deviceSync/wireShadowStore';
import type {
  Trip,
  TripAttachmentRef,
  TripCityOption,
  TripCityVisitMeta,
  TripHotelStay,
  TripTaskItem,
  TripType,
  TripUserPlanSelection,
} from '../types/client';
import { createEmptyTrip, createEmptyTripPreferences } from '../types/client';
import { parseCityVisitMetaFromDevice } from '../utils/tripDayCards';

const STORAGE_PREFIX = 'wt_user_trips_';

/** Cognito sub for trip localStorage / Device Sync writes (AI plan job persistence). */
export function getUserIdForTripWrites(): string | null {
  return getUserSubFromToken();
}

/**
 * Normalize ids so iOS uppercase UUIDs match web lowercase record keys.
 * Non-UUID strings are returned trimmed as-is (lowercased when UUID-shaped).
 */
export function normalizeEntityId(id: string | null | undefined): string {
  const raw = String(id ?? '').trim();
  if (!raw) return '';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
    return raw.toLowerCase();
  }
  return raw;
}

function remapKeyedByNormalizedId<T>(
  input: Record<string, T> | null | undefined
): Record<string, T> {
  if (!input) return {};
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(input)) {
    const id = normalizeEntityId(key);
    if (!id) continue;
    out[id] = value;
  }
  return out;
}

function getStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function loadTrips(userId: string): Trip[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTrip);
  } catch {
    return [];
  }
}

function saveTripsLocal(userId: string, trips: Trip[]): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(trips));
}

function toDeviceTripType(tripType: TripType | null): string | undefined {
  if (!tripType) return undefined;
  const map: Record<TripType, string> = {
    organized_tour: 'Organized Tour',
    cruise: 'Cruise',
    business: 'Business',
    backpacking: 'Backpacking',
    luxury: 'Luxury',
    adventure: 'Adventure',
    cultural: 'Cultural',
    other: 'Other',
  };
  return map[tripType] ?? tripType;
}

function fromDeviceTripType(raw: unknown): TripType | null {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  const aliases: Record<string, TripType> = {
    organized_tour: 'organized_tour',
    organizedtour: 'organized_tour',
    cruise: 'cruise',
    business: 'business',
    backpacking: 'backpacking',
    luxury: 'luxury',
    adventure: 'adventure',
    cultural: 'cultural',
    other: 'other',
  };
  return aliases[s] ?? (s as TripType);
}

function dateOnly(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function mapCityOptionsFromDevice(raw: unknown): TripCityOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((o) => decodeCityOption(o));
}

/** Parse Device Sync / export `taskItems` — mirrors iOS TripTaskItemExportData. */
export function mapTaskItemsFromDevice(raw: unknown): TripTaskItem[] {
  if (!Array.isArray(raw)) return [];
  const items: TripTaskItem[] = raw.map((o, index) => {
    const item = (o && typeof o === 'object' ? o : {}) as Record<string, unknown>;
    const sortRaw = item.sortOrder;
    const sortOrder =
      typeof sortRaw === 'number' && Number.isFinite(sortRaw) ? sortRaw : index;
    return {
      id: normalizeEntityId(String(item.id ?? crypto.randomUUID())) || crypto.randomUUID(),
      title: String(item.title ?? '').trim(),
      isDone: Boolean(item.isDone ?? item.completed),
      sortOrder,
    };
  });
  return items
    .filter((t) => t.title.length > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

/** City display name from a Device Sync city visit body (chunk nested or granular). */
export function extractCityVisitName(body: Record<string, unknown>): string {
  const direct = String(
    body.cityName ?? body.city ?? body.name ?? body.portName ?? ''
  ).trim();
  if (direct) return direct;
  const days = body.days;
  if (Array.isArray(days)) {
    for (const day of days) {
      if (!day || typeof day !== 'object') continue;
      const city = String((day as Record<string, unknown>).city ?? '').trim();
      if (city) return city;
    }
  }
  return '';
}

function cityVisitIdFromRecord(cv: DeviceSyncRecord): string {
  const b = cv.body ?? {};
  const fromBody = normalizeEntityId(String(b.cityVisitId ?? b.id ?? ''));
  if (fromBody) return fromBody;
  const marker = '#cityVisit#';
  const idx = cv.id?.indexOf(marker) ?? -1;
  if (idx >= 0) {
    return normalizeEntityId(cv.id.slice(idx + marker.length));
  }
  return '';
}

/** Merge maps preferring non-empty remote values so blank sync fields do not wipe local names. */
function mergePreferNonEmptyStrings(
  local: Record<string, string> | undefined,
  remote: Record<string, string> | undefined
): Record<string, string> {
  const out: Record<string, string> = { ...(local ?? {}) };
  for (const [key, value] of Object.entries(remote ?? {})) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) {
      out[key] = trimmed;
    } else if (!(key in out)) {
      out[key] = '';
    }
  }
  return out;
}

function tripFromDeviceRecords(
  tripRec: DeviceSyncRecord,
  cityVisits: DeviceSyncRecord[]
): Trip | null {
  const body = tripRec.body ?? {};
  if (body.deleted === true) return null;
  const tripId = normalizeEntityId(String(body.tripId ?? ''));
  if (!tripId) return null;

  const cityVisitIds = Array.isArray(body.cityVisitIds)
    ? body.cityVisitIds.map((id) => normalizeEntityId(String(id))).filter(Boolean)
    : [];
  const cityVisitIdSet = new Set(cityVisitIds);

  const cityNames: Record<string, string> = {};
  const cityOptions: Record<string, TripCityOption[]> = {};
  const cityCoords: Record<string, { latitude: number; longitude: number }> = {};
  const cityVisitMeta: Record<string, TripCityVisitMeta> = {};
  const hotelStaysByCity: Record<string, TripHotelStay[]> = {};
  const itineraryDays: Trip['itineraryDays'] = [];
  for (const cv of cityVisits) {
    const b = cv.body ?? {};
    if (b.deleted === true) continue;
    const cityVisitId = cityVisitIdFromRecord(cv);
    if (!cityVisitId) continue;
    const bodyTripId = normalizeEntityId(String(b.tripId ?? ''));
    // Chunk nested visits omit tripId; match via trip roster or explicit tripId.
    const belongsToTrip =
      (Boolean(bodyTripId) && bodyTripId === tripId) || cityVisitIdSet.has(cityVisitId);
    if (!belongsToTrip) continue;
    const decoded = decodeCityVisit(b);
    const name = extractCityVisitName(b) || decoded.cityName;
    if (name) {
      cityNames[cityVisitId] = name;
    }
    cityOptions[cityVisitId] = mapCityOptionsFromDevice(b.options);
    hotelStaysByCity[cityVisitId] = hotelStaysToTrip(decoded.hotelStays);
    const lat = typeof b.latitude === 'number' ? b.latitude : Number(b.latitude);
    const lng = typeof b.longitude === 'number' ? b.longitude : Number(b.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      cityCoords[cityVisitId] = { latitude: lat, longitude: lng };
    }
    cityVisitMeta[cityVisitId] = parseCityVisitMetaFromDevice(b);
    for (const day of itineraryDaysFromCityVisit(decoded)) {
      const existing = itineraryDays.find((d) => d.date === day.date && d.cityId === day.cityId);
      if (existing) {
        existing.items = [...existing.items, ...day.items];
      } else {
        itineraryDays.push(day);
      }
    }
  }

  let startDate = dateOnly(body.startDate);
  let endDate = dateOnly(body.endDate);
  // When trip shell dates are missing/collapsed, derive span from city visit arrivals.
  const arrivalDates = cityVisitIds
    .map((id) => cityVisitMeta[id]?.arrivalDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  if (arrivalDates.length > 0) {
    if (!startDate) startDate = arrivalDates[0];
    if (!endDate || endDate === startDate) {
      endDate = arrivalDates[arrivalDates.length - 1];
    }
  }

  const travelers =
    typeof body.numberOfTravelers === 'number' ? body.numberOfTravelers : null;

  const userPlanSelections: TripUserPlanSelection[] = Array.isArray(body.userPlanSelections)
    ? (body.userPlanSelections as Record<string, unknown>[]).map((s) => ({
        id: String(s.id ?? ''),
        tripId: normalizeEntityId(String(s.tripId ?? tripId)),
        cityVisitId: normalizeEntityId(String(s.cityVisitId ?? '')),
        itemTitle: String(s.itemTitle ?? ''),
        normalizedTitle: s.normalizedTitle != null ? String(s.normalizedTitle) : undefined,
        selectedSlot: s.selectedSlot != null ? String(s.selectedSlot) : undefined,
        selectedDate: s.selectedDate != null ? String(s.selectedDate) : undefined,
        itemData: s.itemData,
        createdAt: s.createdAt != null ? String(s.createdAt) : undefined,
      }))
    : [];

  const attachmentRefs: TripAttachmentRef[] = Array.isArray(body.attachmentRefs)
    ? (body.attachmentRefs as Record<string, unknown>[]).map((r) => ({
        attachmentId: String(r.attachmentId ?? ''),
        fileId: String(r.fileId ?? ''),
      }))
    : [];

  return normalizeTrip({
    id: tripId,
    title: String(body.name ?? ''),
    tripType: fromDeviceTripType(body.tripType),
    info: String(body.tripDescription ?? body.info ?? ''),
    startDate,
    endDate,
    cityIds: cityVisitIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    cityOptions,
    hotelStaysByCity,
    taskItems: mapTaskItemsFromDevice(body.taskItems),
    homeTimeZone: body.homeTimeZone != null ? String(body.homeTimeZone) : null,
    currency: body.currency != null ? String(body.currency) : null,
    temperatureUnit: body.temperatureUnit != null ? String(body.temperatureUnit) : null,
    distanceUnit: body.distanceUnit != null ? String(body.distanceUnit) : null,
    budget: typeof body.budget === 'number' ? body.budget : null,
    budgetCurrency: body.budgetCurrency != null ? String(body.budgetCurrency) : null,
    ownerId: body.ownerId != null ? String(body.ownerId) : null,
    canEdit: body.canEdit == null ? null : Boolean(body.canEdit),
    userPlanSelections,
    attachmentFileIds: Array.isArray(body.attachmentFileIds)
      ? body.attachmentFileIds.map((id) => String(id))
      : [],
    attachmentRefs,
    previewMapImageFileId:
      body.previewMapImageFileId != null ? String(body.previewMapImageFileId) : null,
    /** Schema 1.3 — copy when present; leave null when absent (never invent "now") */
    contentUpdatedAt: parseContentTimestamp(body.contentUpdatedAt),
    groupSize: travelers,
    adults: travelers,
    children: null,
    childrenAges: [],
    preferences: createEmptyTripPreferences(),
    travelCompanions: [],
    itineraryDays,
  });
}

function buildTripV1Body(
  trip: Trip,
  deleted = false,
  userId?: string | null
): Record<string, unknown> {
  const taskItems = (trip.taskItems ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => ({
      id: t.id,
      title: t.title,
      isDone: t.isDone,
      sortOrder: t.sortOrder,
    }));
  const webBody: Record<string, unknown> = {
    schemaVersion: 1,
    exportSchemaVersion: DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
    tripId: trip.id,
    name: trip.title || 'Untitled trip',
    startDate: trip.startDate ?? undefined,
    endDate: trip.endDate ?? undefined,
    tripType: toDeviceTripType(trip.tripType),
    tripDescription: trip.info || undefined,
    numberOfTravelers: trip.adults ?? trip.groupSize ?? undefined,
    cityVisitIds: trip.cityIds ?? [],
    citySyncMode: 'granular',
    chunkCount: 0,
    deleted,
    taskItems,
  };
  if (trip.homeTimeZone) webBody.homeTimeZone = trip.homeTimeZone;
  if (trip.currency) webBody.currency = trip.currency;
  if (trip.temperatureUnit) webBody.temperatureUnit = trip.temperatureUnit;
  if (trip.distanceUnit) webBody.distanceUnit = trip.distanceUnit;
  if (trip.budget != null) webBody.budget = trip.budget;
  if (trip.budgetCurrency) webBody.budgetCurrency = trip.budgetCurrency;
  // Always export ownership on owned pushes (authoritative contract with iOS).
  webBody.ownerId = trip.ownerId ?? undefined;
  webBody.canEdit = trip.canEdit == null ? true : trip.canEdit;
  if (trip.userPlanSelections?.length) {
    webBody.userPlanSelections = trip.userPlanSelections;
  }
  if (trip.attachmentFileIds?.length) {
    webBody.attachmentFileIds = trip.attachmentFileIds;
  }
  if (trip.attachmentRefs?.length) {
    webBody.attachmentRefs = trip.attachmentRefs;
  }
  if (trip.previewMapImageFileId) {
    webBody.previewMapImageFileId = trip.previewMapImageFileId;
  }
  if (trip.contentUpdatedAt) {
    webBody.contentUpdatedAt = trip.contentUpdatedAt;
  }

  const shadow = userId ? getTripWireShadow(userId, trip.id) : null;
  const merged = mergeWireBodies(shadow, webBody);
  // Web is granular — keep chunkCount/citySyncMode from web overlay
  merged.citySyncMode = 'granular';
  merged.chunkCount = 0;
  merged.taskItems = taskItems;
  merged.cityVisitIds = trip.cityIds ?? [];
  merged.deleted = deleted;
  return merged;
}

function buildCityVisitBody(
  trip: Trip,
  cityVisitId: string,
  userId?: string | null
): Record<string, unknown> {
  const webBody = buildGranularCityVisitBody(trip, cityVisitId);
  const id = normalizeEntityId(cityVisitId);
  const shadow = userId ? getCityWireShadow(userId, id) : null;
  return mergeCityVisitWireWithWebEdits(shadow, webBody);
}

async function upsertCatalogEntry(sub: string, tripId: string, deleted: boolean): Promise<void> {
  const catalogId = catalogRecordId(sub);
  let trips: Array<Record<string, unknown>> = [];
  try {
    const existing = await getDeviceSync(catalogId);
    const list = existing?.body?.trips;
    if (Array.isArray(list)) {
      trips = list.map((t) => ({ ...(t as Record<string, unknown>) }));
    }
  } catch (err) {
    console.warn('tripsService: catalog get failed', err);
  }

  const now = new Date().toISOString();
  const idx = trips.findIndex((t) => String(t.tripId) === tripId);
  const entry = {
    tripId,
    updatedAt: now,
    deleted,
    syncComplete: true,
  };
  if (idx >= 0) trips[idx] = { ...trips[idx], ...entry };
  else trips.push(entry);

  await putDeviceSync(
    catalogId,
    'sync-catalog-v1',
    {
      schemaVersion: 1,
      trips,
    },
    sub
  );
}

/**
 * Push one trip (+ city visits + catalog entry) to Device Sync.
 * Prefer passing `userId` (Auth `sub`) so import/create does not depend on token parse timing.
 */
export async function syncTripToDevice(
  trip: Trip,
  options: {
    deleted?: boolean;
    userId?: string | null;
    /** City visit ids removed since last sync — push deleted:true tombstones. */
    removedCityVisitIds?: string[];
  } = {}
): Promise<void> {
  const deleted = Boolean(options.deleted);
  const sub = options.userId || getUserSubFromToken();
  if (!sub) {
    console.warn('tripsService: no Cognito sub; skip device sync push');
    return;
  }

  let toPush = trip;
  const owner = trip.ownerId != null ? String(trip.ownerId).trim() : '';
  if (owner && owner !== sub) {
    console.warn(
      'tripsService: skip device sync push for foreign owner',
      trip.id,
      'ownerId=',
      owner
    );
    return;
  }
  if (!owner) {
    toPush = claimNilOwnerTrip(trip, sub);
    // Persist claim so local cache matches what we push.
    const trips = loadTrips(sub);
    const idx = trips.findIndex((t) => normalizeEntityId(t.id) === normalizeEntityId(trip.id));
    if (idx >= 0) {
      trips[idx] = { ...trips[idx], ownerId: sub, canEdit: true };
      saveTripsLocal(sub, trips);
    }
  }

  console.info(
    `tripsService: device sync ${deleted ? 'tombstone' : 'push'} trip=${toPush.id} cities=${toPush.cityIds?.length ?? 0}`
  );

  const tripBody = buildTripV1Body(toPush, deleted, sub);
  await putDeviceSync(tripRecordId(sub, toPush.id), 'trip-v1', tripBody, sub);
  if (!deleted) {
    setTripWireShadow(sub, toPush.id, tripBody);
  }

  if (!deleted) {
    for (const cityVisitId of toPush.cityIds ?? []) {
      const body = buildCityVisitBody(toPush, cityVisitId, sub);
      await putDeviceSync(cityVisitRecordId(sub, cityVisitId), 'cityVisit-v1', body, sub);
      setCityWireShadow(sub, normalizeEntityId(cityVisitId), body);
    }
    for (const removedId of options.removedCityVisitIds ?? []) {
      if (!removedId) continue;
      await putDeviceSync(
        cityVisitRecordId(sub, removedId),
        'cityVisit-v1',
        {
          schemaVersion: 1,
          exportSchemaVersion: DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
          tripId: toPush.id,
          cityVisitId: removedId,
          deleted: true,
        },
        sub
      );
    }
  }

  await upsertCatalogEntry(sub, toPush.id, deleted);
}

export function mergeRemoteOverLocal(local: Trip[], remote: Trip[]): Trip[] {
  const byId = new Map<string, Trip>();
  const remoteList = [...remote];
  for (const t of remote) byId.set(normalizeEntityId(t.id), t);
  for (const t of local) {
    const localId = normalizeEntityId(t.id);
    const remoteTrip = byId.get(localId);
    if (!remoteTrip) {
      // Drop local-only trips that fingerprint-match a remote trip (re-import orphans).
      const remoteMatch = remoteList.find((r) =>
        tripsFingerprintMatch(
          {
            title: t.title,
            tripType: t.tripType,
            startDate: t.startDate,
            endDate: t.endDate,
            sourceItineraryId: t.sourceItineraryId,
          },
          {
            title: r.title,
            tripType: r.tripType,
            startDate: r.startDate,
            endDate: r.endDate,
            sourceItineraryId: r.sourceItineraryId,
          }
        )
      );
      if (remoteMatch) {
        console.warn(
          'tripsService: dropping local duplicate trip',
          localId,
          'preferring remote',
          normalizeEntityId(remoteMatch.id)
        );
        continue;
      }
      byId.set(localId, t);
      continue;
    }
    // Prefer remote shell; keep richer local planning fields when remote has none
    byId.set(localId, {
      ...remoteTrip,
      id: localId,
      sourceItineraryId:
        remoteTrip.sourceItineraryId ?? t.sourceItineraryId ?? null,
      cityNames: mergePreferNonEmptyStrings(
        remapKeyedByNormalizedId(t.cityNames),
        remapKeyedByNormalizedId(remoteTrip.cityNames)
      ),
      cityCoords: {
        ...remapKeyedByNormalizedId(t.cityCoords),
        ...remapKeyedByNormalizedId(remoteTrip.cityCoords),
      },
      cityVisitMeta: {
        ...remapKeyedByNormalizedId(t.cityVisitMeta),
        ...remapKeyedByNormalizedId(remoteTrip.cityVisitMeta),
      },
      cityOptions: {
        ...remapKeyedByNormalizedId(t.cityOptions),
        ...remapKeyedByNormalizedId(remoteTrip.cityOptions),
      },
      hotelStaysByCity: {
        ...remapKeyedByNormalizedId(t.hotelStaysByCity),
        ...remapKeyedByNormalizedId(remoteTrip.hotelStaysByCity),
      },
      taskItems:
        (remoteTrip.taskItems?.length ?? 0) > 0
          ? remoteTrip.taskItems
          : t.taskItems ?? [],
      userPlanSelections:
        (remoteTrip.userPlanSelections?.length ?? 0) > 0
          ? remoteTrip.userPlanSelections
          : t.userPlanSelections ?? [],
      itineraryDays:
        (remoteTrip.itineraryDays?.length ?? 0) > 0
          ? remoteTrip.itineraryDays
          : t.itineraryDays,
      cityHistories: {
        ...remapKeyedByNormalizedId(t.cityHistories),
        ...remapKeyedByNormalizedId(remoteTrip.cityHistories),
      },
      homeTimeZone: remoteTrip.homeTimeZone ?? t.homeTimeZone ?? null,
      currency: remoteTrip.currency ?? t.currency ?? null,
      temperatureUnit: remoteTrip.temperatureUnit ?? t.temperatureUnit ?? null,
      distanceUnit: remoteTrip.distanceUnit ?? t.distanceUnit ?? null,
      budget: remoteTrip.budget ?? t.budget ?? null,
      budgetCurrency: remoteTrip.budgetCurrency ?? t.budgetCurrency ?? null,
      ownerId: remoteTrip.ownerId ?? t.ownerId ?? null,
      canEdit: remoteTrip.canEdit ?? t.canEdit ?? null,
      attachmentFileIds:
        (remoteTrip.attachmentFileIds?.length ?? 0) > 0
          ? remoteTrip.attachmentFileIds
          : t.attachmentFileIds ?? [],
      attachmentRefs:
        (remoteTrip.attachmentRefs?.length ?? 0) > 0
          ? remoteTrip.attachmentRefs
          : t.attachmentRefs ?? [],
      previewMapImageFileId:
        remoteTrip.previewMapImageFileId ?? t.previewMapImageFileId ?? null,
      // Prefer remote content stamp when present; keep local when remote omits (old schema)
      contentUpdatedAt:
        remoteTrip.contentUpdatedAt ?? t.contentUpdatedAt ?? null,
    });
  }
  return Array.from(byId.values());
}

export function normalizeTrip(raw: unknown): Trip {
  const empty = createEmptyTrip();
  const t = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const cityOptionsRaw = t.cityOptions;
  let cityOptions: Record<string, TripCityOption[]> = {};
  if (cityOptionsRaw && typeof cityOptionsRaw === 'object' && !Array.isArray(cityOptionsRaw)) {
    cityOptions = Object.fromEntries(
      Object.entries(cityOptionsRaw as Record<string, unknown>).map(([cityId, opts]) => [
        normalizeEntityId(cityId),
        Array.isArray(opts)
          ? opts.map((o) => {
              const opt = (o && typeof o === 'object' ? o : {}) as Record<string, unknown>;
              return {
                id: String(opt.id ?? crypto.randomUUID()),
                title: String(opt.title ?? ''),
                description: opt.description != null ? String(opt.description) : undefined,
                url: opt.url != null ? String(opt.url) : undefined,
                completed: Boolean(opt.completed),
                address: opt.address != null ? String(opt.address) : undefined,
                order: typeof opt.order === 'number' ? opt.order : undefined,
              };
            })
          : [],
      ])
    );
  }

  const cityNamesRaw = t.cityNames;
  const cityNames =
    cityNamesRaw && typeof cityNamesRaw === 'object' && !Array.isArray(cityNamesRaw)
      ? Object.fromEntries(
          Object.entries(cityNamesRaw as Record<string, unknown>).map(([k, v]) => [
            normalizeEntityId(k),
            String(v ?? ''),
          ])
        )
      : {};

  const cityCoordsRaw = t.cityCoords;
  const cityCoords: Record<string, { latitude: number; longitude: number }> = {};
  if (cityCoordsRaw && typeof cityCoordsRaw === 'object' && !Array.isArray(cityCoordsRaw)) {
    for (const [id, value] of Object.entries(cityCoordsRaw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const c = value as Record<string, unknown>;
      const lat = typeof c.latitude === 'number' ? c.latitude : Number(c.latitude);
      const lng = typeof c.longitude === 'number' ? c.longitude : Number(c.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        cityCoords[normalizeEntityId(id)] = { latitude: lat, longitude: lng };
      }
    }
  }

  const cityVisitMetaRaw = t.cityVisitMeta;
  const cityVisitMeta: Record<string, TripCityVisitMeta> = {};
  if (cityVisitMetaRaw && typeof cityVisitMetaRaw === 'object' && !Array.isArray(cityVisitMetaRaw)) {
    for (const [id, value] of Object.entries(cityVisitMetaRaw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      cityVisitMeta[normalizeEntityId(id)] = parseCityVisitMetaFromDevice(
        value as Record<string, unknown>
      );
    }
  }

  const cityHistoriesRaw = t.cityHistories;
  const cityHistories =
    cityHistoriesRaw && typeof cityHistoriesRaw === 'object' && !Array.isArray(cityHistoriesRaw)
      ? remapKeyedByNormalizedId(cityHistoriesRaw as Trip['cityHistories'])
      : {};

  const cityIds = Array.isArray(t.cityIds)
    ? t.cityIds.map((id) => normalizeEntityId(String(id))).filter(Boolean)
    : [];

  return {
    id: normalizeEntityId(String(t.id ?? crypto.randomUUID())) || crypto.randomUUID(),
    title: String(t.title ?? empty.title),
    tripType: (t.tripType as TripType | null) ?? null,
    info: String(t.info ?? ''),
    startDate: t.startDate != null ? String(t.startDate) : null,
    endDate: t.endDate != null ? String(t.endDate) : null,
    cityIds,
    cityNames,
    cityCoords,
    cityVisitMeta,
    cityHistories,
    cityOptions,
    groupSize: typeof t.groupSize === 'number' ? t.groupSize : null,
    adults: typeof t.adults === 'number' ? t.adults : null,
    children: typeof t.children === 'number' ? t.children : null,
    childrenAges: Array.isArray(t.childrenAges)
      ? t.childrenAges.map((n) => Number(n)).filter((n) => !Number.isNaN(n))
      : [],
    preferences:
      t.preferences && typeof t.preferences === 'object'
        ? { ...createEmptyTripPreferences(), ...(t.preferences as object) }
        : createEmptyTripPreferences(),
    travelCompanions: Array.isArray(t.travelCompanions) ? (t.travelCompanions as Trip['travelCompanions']) : [],
    itineraryDays: Array.isArray(t.itineraryDays) ? (t.itineraryDays as Trip['itineraryDays']) : [],
    taskItems: mapTaskItemsFromDevice(t.taskItems),
    hotelStaysByCity: remapKeyedByNormalizedId(
      (t.hotelStaysByCity as Trip['hotelStaysByCity']) ?? {}
    ),
    homeTimeZone: t.homeTimeZone != null ? String(t.homeTimeZone) : null,
    currency: t.currency != null ? String(t.currency) : null,
    temperatureUnit: t.temperatureUnit != null ? String(t.temperatureUnit) : null,
    distanceUnit: t.distanceUnit != null ? String(t.distanceUnit) : null,
    budget: typeof t.budget === 'number' ? t.budget : null,
    budgetCurrency: t.budgetCurrency != null ? String(t.budgetCurrency) : null,
    ownerId: t.ownerId != null ? String(t.ownerId) : null,
    canEdit: t.canEdit == null ? null : Boolean(t.canEdit),
    userPlanSelections: Array.isArray(t.userPlanSelections)
      ? (t.userPlanSelections as TripUserPlanSelection[])
      : [],
    attachmentFileIds: Array.isArray(t.attachmentFileIds)
      ? t.attachmentFileIds.map((id) => String(id))
      : [],
    attachmentRefs: Array.isArray(t.attachmentRefs)
      ? (t.attachmentRefs as TripAttachmentRef[])
      : [],
    previewMapImageFileId:
      t.previewMapImageFileId != null ? String(t.previewMapImageFileId) : null,
    sourceItineraryId:
      t.sourceItineraryId != null && String(t.sourceItineraryId).trim()
        ? String(t.sourceItineraryId).trim()
        : null,
    contentUpdatedAt: parseContentTimestamp(t.contentUpdatedAt),
  };
}

/**
 * Pull trips from Device Sync into localStorage using apply LWW (catalog + chunk/granular).
 * Call on login so web shows the same trips as the iOS app.
 */
export async function pullTripsFromBackend(userId: string, _email: string): Promise<void> {
  try {
    // Claim any nil-owner local trips for this user before merge (never re-home others).
    claimNilOwnerTripsForUser(userId);

    const records = await fetchAllDeviceSyncChanges();
    const outcome = applyDeviceSyncRecords(records);

    const incompleteIds = outcome.incompleteCatalogTripIds;
    console.info(
      `tripsService: device sync pull records=${records.length} appliedTrips=${outcome.trips.size}` +
        ` incompleteCatalog=${incompleteIds.length}`
    );
    if (incompleteIds.length > 0) {
      console.warn(
        'tripsService: skipping incomplete Device Sync trips (catalog syncComplete=false):',
        incompleteIds
      );
    }

    // Map applied trips → Trip models; expand city visits for tripFromDeviceRecords
    const tripRecs: DeviceSyncRecord[] = [];
    const cityVisits: DeviceSyncRecord[] = [];

    for (const record of records) {
      if (record.type === 'trip-v1') {
        const tripId = normalizeEntityId(String(record.body?.tripId ?? ''));
        if (tripId && incompleteIds.includes(tripId)) continue;
        if (outcome.trips.has(tripId) || record.body?.deleted === true) {
          tripRecs.push(record);
        }
      }
    }

    // Prefer applied city visit bodies (LWW winners)
    Array.from(outcome.trips.entries()).forEach(([tripId, applied]) => {
      // Ensure a trip header exists for mapping
      if (!tripRecs.some((r) => normalizeEntityId(String(r.body?.tripId ?? '')) === tripId)) {
        tripRecs.push({
          id: tripRecordId(userId, tripId),
          type: 'trip-v1',
          body: {
            tripId,
            name: applied.name || 'Trip',
            cityVisitIds: Array.from(applied.cityVisits.keys()),
            chunkCount: applied.chunkCount,
            deleted: false,
          },
        });
      }

      setTripWireShadow(userId, tripId, {
        tripId,
        name: applied.name,
        chunkCount: applied.chunkCount,
        cityVisitIds: Array.from(applied.cityVisits.keys()),
      });

      Array.from(applied.cityVisits.entries()).forEach(([cityId, visit]) => {
        const body = encodeCityVisit(visit);
        body.tripId = tripId;
        cityVisits.push({
          id: cityVisitRecordId(userId, cityId),
          type: 'cityVisit-v1',
          body,
        });
        setCityWireShadow(userId, cityId, body);
      });
    });

    // Also include trip headers that were skipped? No — incomplete catalog trips stay out.

    // Merge trip header bodies from records onto shadows for shell fields
    for (const tr of records.filter((r) => r.type === 'trip-v1')) {
      const tripId = normalizeEntityId(String(tr.body?.tripId ?? ''));
      if (!tripId || !outcome.trips.has(tripId)) continue;
      if (tr.body?.deleted === true) continue;
      setTripWireShadow(userId, tripId, mergeWireBodies(getTripWireShadow(userId, tripId), tr.body));
      // Replace tripRec entry with full header body when available
      const idx = tripRecs.findIndex(
        (r) => normalizeEntityId(String(r.body?.tripId ?? '')) === tripId
      );
      if (idx >= 0) tripRecs[idx] = tr;
      else tripRecs.push(tr);
    }

    const remoteTrips = tripRecs
      .map((tr) => tripFromDeviceRecords(tr, cityVisits))
      .filter((t): t is Trip => t != null);

    const local = loadTrips(userId);
    const merged = mergeRemoteOverLocal(local, remoteTrips);
    // Claim remaining nils after merge; preserve foreign ownerId from payload.
    const claimed = claimNilOwnerTrips(merged, userId);
    saveTripsLocal(userId, claimed);
  } catch (err) {
    console.warn('tripsService: device sync pull failed', err);
  }
}

/**
 * Assign ownerId = userId on local trips with missing ownerId. Does not change
 * trips that already have a different ownerId.
 */
export function claimNilOwnerTripsForUser(userId: string): void {
  const trips = loadTrips(userId);
  const claimed = claimNilOwnerTrips(trips, userId);
  if (claimed !== trips) {
    saveTripsLocal(userId, claimed);
  }
}

/** Push all local trips to Device Sync (fire-and-forget helper). */
export function pushTripsToBackend(userId: string): void {
  claimNilOwnerTripsForUser(userId);
  const trips = loadTrips(userId);
  if (trips.length === 0) return;
  const skipIds = orphanTripIdsToSkipPush(trips);
  (async () => {
    for (const trip of trips) {
      if (skipIds.has(trip.id)) {
        console.warn(
          'tripsService: skipping push of fingerprint-duplicate trip',
          trip.id
        );
        continue;
      }
      if (!isOwnedTrip(trip, userId)) {
        console.warn(
          'tripsService: skipping push of non-owned trip',
          trip.id,
          'ownerId=',
          trip.ownerId
        );
        continue;
      }
      try {
        await syncTripToDevice(trip, { userId });
      } catch (err) {
        console.warn('tripsService: device sync push failed for trip', trip.id, err);
      }
    }
  })();
}

function saveTrips(userId: string, trips: Trip[]): void {
  saveTripsLocal(userId, trips);
}

export function getAllTrips(userId: string): Trip[] {
  return loadTrips(userId);
}

/** Trips accessible to the signed-in user (owned + active shares). Claims nils first. */
export function getVisibleTrips(userId: string): Trip[] {
  claimNilOwnerTripsForUser(userId);
  return visibleTrips(loadTrips(userId), userId, getActiveSharedTripIds(userId));
}

export function getTrip(userId: string, tripId: string): Trip | null {
  const id = normalizeEntityId(tripId);
  return loadTrips(userId).find((t) => normalizeEntityId(t.id) === id) ?? null;
}

/** Like getTrip, but returns null when the trip is not accessible to this user. */
export function getAccessibleTrip(userId: string, tripId: string): Trip | null {
  claimNilOwnerTripsForUser(userId);
  const trip = getTrip(userId, tripId);
  if (!trip) return null;
  if (!isAccessibleTrip(trip, userId, getActiveSharedTripIds(userId))) return null;
  return trip;
}

export interface CreateTripOptions {
  /**
   * When true, skip the background Device Sync push so the caller can
   * `await syncTripToDevice` (used by cruise import).
   */
  deferDeviceSync?: boolean;
}

export function createTrip(
  userId: string,
  data: Omit<Trip, 'id'>,
  options: CreateTripOptions = {}
): Trip {
  const trips = loadTrips(userId);
  const incomingTasks = data.taskItems ?? [];
  const taskItems =
    incomingTasks.length > 0 ? incomingTasks : taskItemsFromDefaultTemplate();
  const trip = stampTripContent(
    normalizeTrip({
      ...data,
      id: crypto.randomUUID(),
      ownerId: userId,
      canEdit: true,
      taskItems,
    }),
    {
      stampAllDays: Boolean(data.itineraryDays?.length),
    }
  );
  trips.push(trip);
  saveTrips(userId, trips);
  if (!options.deferDeviceSync) {
    syncTripToDevice(trip, { userId }).catch((err) => {
      console.warn('tripsService: create push failed', err);
    });
  }
  return trip;
}

export function updateTrip(
  userId: string,
  tripId: string,
  updates: Partial<Omit<Trip, 'id'>>,
  syncOptions: { removedCityVisitIds?: string[] } = {}
): Trip | null {
  const id = normalizeEntityId(tripId);
  const trips = loadTrips(userId);
  const idx = trips.findIndex((t) => normalizeEntityId(t.id) === id);
  if (idx === -1) return null;
  let next = normalizeTrip({ ...trips[idx], ...updates, id });
  // Local content mutations stamp; pull/merge does not use updateTrip so sync won't re-stamp.
  // If caller explicitly sets contentUpdatedAt (rare), preserve it.
  if (!('contentUpdatedAt' in updates)) {
    next = stampTripContent(next, {
      dayDates: updates.itineraryDays?.map((d) => d.date),
    });
  }
  trips[idx] = next;
  saveTrips(userId, trips);
  syncTripToDevice(trips[idx], {
    userId,
    removedCityVisitIds: syncOptions.removedCityVisitIds,
  }).catch((err) => {
    console.warn('tripsService: update push failed', err);
  });
  return trips[idx];
}

export function deleteTrip(userId: string, tripId: string): boolean {
  const id = normalizeEntityId(tripId);
  const trips = loadTrips(userId);
  const existing = trips.find((t) => normalizeEntityId(t.id) === id);
  const filtered = trips.filter((t) => normalizeEntityId(t.id) !== id);
  if (filtered.length === trips.length) return false;
  saveTrips(userId, filtered);
  if (existing) {
    syncTripToDevice(existing, { deleted: true, userId }).catch((err) => {
      console.warn('tripsService: delete push failed', err);
    });
  }
  return true;
}

export { createEmptyTrip };
